import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { type GorgiasInfo, IMPORT_STATE } from '../types';

export const useGorgias = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;
  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(
    SOURCE_TYPE.GORGIAS,
    workspaceAlias,
  );

  const { data: gorgiasWorkspace, refetch } = useQuery({
    queryKey: queryKeys,
    queryFn: async () => {
      const data = await service.gorgias.getGorgiasInfo(workspaceAlias);
      if (!data) return null;

      return {
        ...data,
        import_state: data.import_state ?? data.state,
        history: data.history ?? [],
        name: data.name ?? data.domain,
      } as GorgiasInfo;
    },
    enabled,
  });

  const { mutate: disconnectGorgiasMutation } = useMutation({
    mutationFn: () =>
      service.gorgias.deleteGorgiasInfo(gorgiasWorkspace?.alias || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: (dateRange: NullableDateRangeT) =>
      service.gorgias.importWorkspaceFeedback(
        gorgiasWorkspace?.alias || '',
        dateRange.start_date || '',
        dateRange.end_date || '',
      ),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys, (old: GorgiasInfo | null) => {
        if (!old) return old;
        return {
          ...old,
          import_state: IMPORT_STATE.IMPORTING,
        };
      });
    },
  });

  return {
    gorgiasWorkspace,
    disconnectGorgiasMutation,
    requestImportMutation,
    refetch,
  };
};
